import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ChevronDown, HelpCircle, GripVertical } from "lucide-react";
import { api } from "@/lib/api";

const inputCls = "w-full bg-[#141414] border border-[#2a2a2a] px-3.5 py-2.5 text-[13px] text-[#e8e8e8] rounded focus:outline-none focus:border-[#4deb7a] transition-colors";
const labelCls = "block font-mono-dm uppercase tracking-[0.1em] text-[9px] text-[#8f8f8f] mb-1.5";

function QAModal({ section, initial, onClose, onSaved }) {
  const [form, setForm] = useState({ question: initial?.question || "", answer: initial?.answer || "" });
  const save = async (e) => {
    e.preventDefault();
    if (!form.question.trim()) { toast.error("Question is required"); return; }
    try {
      if (initial?.id) await api.put(`/admin/qa/${initial.id}`, form);
      else await api.post(`/admin/sections/${section.id}/qa`, form);
      toast.success(initial?.id ? "Updated" : "Question added");
      onSaved();
    } catch { toast.error("Failed to save"); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] px-5 py-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#0e0e0e] border border-[#222] rounded w-full max-w-lg p-6 my-auto" onClick={(e) => e.stopPropagation()} data-testid="qa-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif-pf text-[24px] text-[#f0f0f0]">{initial?.id ? "Edit" : "New"} question</h3>
          <button onClick={onClose} className="text-[#8f8f8f] hover:text-[#e8e8e8]"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div><label className={labelCls}>Question</label><textarea rows={2} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} className={inputCls} placeholder="The hard question investors ask…" data-testid="qa-question-input" /></div>
          <div><label className={labelCls}>Answer</label><textarea rows={6} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} className={inputCls} placeholder="The direct, evidence-backed response…" data-testid="qa-answer-input" /></div>
          <div className="flex gap-2 pt-1">
            <button type="submit" className="bg-[#4deb7a] text-[#080808] font-medium text-[13px] px-5 py-2.5 rounded" data-testid="qa-save">{initial?.id ? "Save changes" : "Add question"}</button>
            <button type="button" onClick={onClose} className="text-[13px] text-[#bdbdbd] px-3">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function QACard({ item, isAdmin, onEdit, onDelete, dragProps, dragging }) {
  const [open, setOpen] = useState(false);
  return (
    <div {...(dragProps || {})} className={`bg-[#0e0e0e] border border-[#222] rounded overflow-hidden transition-opacity hover:border-[#2a2a2a] ${dragging ? "opacity-40" : ""}`} data-testid={`qa-item-${item.id}`}>
      <div className="flex items-start">
        {isAdmin && dragProps && (
          <span data-testid={`qa-drag-${item.id}`} className="cursor-grab active:cursor-grabbing text-[#5a5a5a] hover:text-[#bdbdbd] pl-3 py-5 shrink-0" title="Drag to reorder">
            <GripVertical className="h-4 w-4" strokeWidth={1.5} />
          </span>
        )}
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid={`qa-toggle-${item.id}`}
          className="flex-1 flex items-start gap-3 text-left px-5 py-4"
          aria-expanded={open}
        >
          <HelpCircle className="h-4 w-4 text-[#4deb7a] shrink-0 mt-0.5" strokeWidth={1.5} />
          <span className="flex-1 text-[14px] text-[#f0f0f0] font-medium leading-snug">{item.question}</span>
          <ChevronDown className={`h-4 w-4 text-[#8f8f8f] shrink-0 mt-0.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
        </button>
        {isAdmin && (
          <div className="flex items-center gap-1.5 px-3 py-4 shrink-0">
            <button data-testid={`qa-edit-${item.id}`} onClick={onEdit} className="text-[#8f8f8f] hover:text-[#e8e8e8]"><Pencil className="h-3.5 w-3.5" /></button>
            <button data-testid={`qa-delete-${item.id}`} onClick={onDelete} className="text-[#8f8f8f] hover:text-[#c83232]"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
      {open && (
        <div className="px-5 pb-5 pt-0 pl-12" data-testid={`qa-answer-${item.id}`}>
          <div className="text-[13px] text-[#d4d4d4] leading-[1.75] whitespace-pre-line border-t border-[#1a1a1a] pt-4">
            {item.answer || <span className="text-[#8f8f8f] italic">No answer added yet.</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ObjectionMemo({ section, isAdmin, onChanged }) {
  const [items, setItems] = useState(section.qa_items || []);
  const [modal, setModal] = useState(null);
  const [dragId, setDragId] = useState(null);
  useEffect(() => { setItems(section.qa_items || []); }, [section.qa_items]);

  const del = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    await api.delete(`/admin/qa/${id}`);
    toast.success("Removed");
    onChanged?.();
  };

  const handleDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const arr = [...items];
    const from = arr.findIndex((q) => q.id === dragId);
    const to = arr.findIndex((q) => q.id === targetId);
    if (from === -1 || to === -1) { setDragId(null); return; }
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setItems(arr);
    setDragId(null);
    try { await api.put(`/admin/qa/reorder`, { order: arr.map((q) => q.id) }); onChanged?.(); }
    catch { toast.error("Failed to save order"); setItems(section.qa_items || []); }
  };

  return (
    <div data-testid="objection-memo">
      {isAdmin && (
        <div className="mb-6">
          <button data-testid="qa-add-btn" onClick={() => setModal({ new: true })} className="inline-flex items-center gap-2 bg-[#4deb7a] hover:bg-[#3dd869] text-[#080808] text-[12px] font-medium px-4 py-2.5 rounded transition-colors">
            <Plus className="h-4 w-4" strokeWidth={1.5} /> Add new
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="bg-[#0e0e0e] border border-[#222] rounded-md py-12 px-8 text-center">
          <HelpCircle className="h-7 w-7 mx-auto text-[#8f8f8f]" strokeWidth={1.25} />
          <div className="text-[13px] text-[#d4d4d4] font-medium mt-3">No questions yet</div>
          <div className="text-[11px] text-[#8f8f8f]">{isAdmin ? "Add your first objection and response above." : "Responses to common investor questions will appear here soon."}</div>
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map((item) => (
            <QACard
              key={item.id}
              item={item}
              isAdmin={isAdmin}
              dragging={dragId === item.id}
              dragProps={isAdmin ? {
                draggable: true,
                onDragStart: () => setDragId(item.id),
                onDragOver: (e) => e.preventDefault(),
                onDrop: () => handleDrop(item.id),
              } : null}
              onEdit={() => setModal({ item })}
              onDelete={() => del(item.id)}
            />
          ))}
        </div>
      )}

      {modal && <QAModal section={section} initial={modal.item} onClose={() => setModal(null)} onSaved={() => { setModal(null); onChanged?.(); }} />}
    </div>
  );
}
